import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import supabase from '../utils/supabaseClient';
import './UnitsManagement.css';

const UnitsManagement = () => {
    const { isDarkTheme } = useTheme();
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingUnit, setEditingUnit] = useState(null);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });

    // Form fields
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');

    const fetchUnits = useCallback(async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('units')
                .select('*, exercises(count)')
                .order('title', { ascending: true });

            if (error) throw error;
            setUnits(data || []);
        } catch (err) {
            console.error('[UnitsManagement] Error:', err);
            // Fallback without count
            try {
                const { data } = await supabase
                    .from('units')
                    .select('*')
                    .order('title', { ascending: true });
                setUnits(data || []);
            } catch (e) {
                console.error('[UnitsManagement] Fallback error:', e);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUnits();
    }, [fetchUnits]);

    const resetForm = () => {
        setTitle('');
        setDescription('');
        setEditingUnit(null);
        setShowForm(false);
    };

    const openCreateForm = () => {
        resetForm();
        setShowForm(true);
    };

    const openEditForm = (unit) => {
        setEditingUnit(unit);
        setTitle(unit.title || '');
        setDescription(unit.description || '');
        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title.trim()) {
            setMessage({ text: 'Title is required.', type: 'error' });
            return;
        }

        setSaving(true);
        setMessage({ text: '', type: '' });

        try {
            const payload = {
                title: title.trim(),
                description: description.trim(),
                updated_at: new Date().toISOString(),
            };

            if (editingUnit) {
                const { error } = await supabase
                    .from('units')
                    .update(payload)
                    .eq('id', editingUnit.id);
                if (error) throw error;
                setMessage({ text: 'Unit updated successfully!', type: 'success' });
            } else {
                payload.created_at = new Date().toISOString();
                const { error } = await supabase
                    .from('units')
                    .insert(payload);
                if (error) throw error;
                setMessage({ text: 'Unit created successfully!', type: 'success' });
            }

            resetForm();
            fetchUnits();
        } catch (err) {
            console.error('[UnitsManagement] Save error:', err);
            const raw = err?.message || 'Failed to save unit.';
            if (raw.toLowerCase().includes('row-level security')) {
                setMessage({
                    text: 'Permission denied by database policy (RLS). Ask an admin to add INSERT/UPDATE policies for `units`.',
                    type: 'error',
                });
            } else {
                setMessage({ text: raw, type: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (unitId) => {
        if (!window.confirm('Are you sure you want to delete this unit? Exercises inside will be unlinked.')) return;

        try {
            const { error } = await supabase
                .from('units')
                .delete()
                .eq('id', unitId);
            if (error) throw error;
            setMessage({ text: 'Unit deleted.', type: 'success' });
            fetchUnits();
        } catch (err) {
            console.error('[UnitsManagement] Delete error:', err);
            setMessage({ text: err.message || 'Failed to delete unit.', type: 'error' });
        }
    };

    return (
        <div className={`units-mgmt ${isDarkTheme ? 'dark' : 'light'}`}>
            <div className="units-header">
                <div className="units-header-left">
                    <h2>Units Management</h2>
                    <span className="units-count-badge">{units.length} units</span>
                </div>
                <button className="admin-action-btn primary" onClick={openCreateForm}>
                    + New Unit
                </button>
            </div>

            {message.text && (
                <div className={`units-message ${message.type}`}>
                    {message.text}
                </div>
            )}

            {showForm && (
                <div className="units-form-card card">
                    <h3>{editingUnit ? 'Edit Unit' : 'Create Unit'}</h3>
                    <form onSubmit={handleSubmit} className="units-form">
                        <div className="units-form-field">
                            <label htmlFor="unit-title">Title *</label>
                            <input
                                id="unit-title"
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="e.g. Introduction to Algebra"
                                required
                            />
                        </div>
                        <div className="units-form-field">
                            <label htmlFor="unit-description">Description</label>
                            <textarea
                                id="unit-description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Brief description of this unit"
                                rows={3}
                            />
                        </div>
                        <div className="units-form-actions">
                            <button type="submit" className="admin-action-btn primary" disabled={saving}>
                                {saving ? 'Saving...' : (editingUnit ? 'Update Unit' : 'Create Unit')}
                            </button>
                            <button type="button" className="admin-action-btn secondary" onClick={resetForm}>
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {loading ? (
                <div className="admin-loading">Loading units...</div>
            ) : units.length === 0 ? (
                <div className="units-empty card">
                    <p>No units yet. Create your first unit to organize exercises.</p>
                    <button className="admin-action-btn primary" onClick={openCreateForm}>
                        + Create Unit
                    </button>
                </div>
            ) : (
                <div className="units-list">
                    {units.map((unit, index) => (
                        <div key={unit.id} className="units-item card">
                            <div className="units-item-left">
                                <span className="units-item-order">#{index + 1}</span>
                                <div className="units-item-info">
                                    <div className="units-item-title">{unit.title}</div>
                                    {unit.description && (
                                        <div className="units-item-desc">{unit.description}</div>
                                    )}
                                    <div className="units-item-meta">
                                        <span className="units-meta-tag count">
                                            {unit.exercises?.[0]?.count ?? '?'} exercises
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="units-item-actions">
                                <button
                                    className="units-action-btn"
                                    onClick={() => openEditForm(unit)}
                                    title="Edit"
                                >
                                    ✏️
                                </button>
                                <button
                                    className="units-action-btn danger"
                                    onClick={() => handleDelete(unit.id)}
                                    title="Delete"
                                >
                                    🗑️
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default UnitsManagement;
